import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Play, RotateCcw, Brain, Shield, Zap, Heart, AlertTriangle,
  Loader2, ChevronRight, Users, Activity, Eye, TrendingUp,
  Pause, SkipForward, SkipBack, Droplets, Layers, Pill, X
} from 'lucide-react';

const PERSONA_ICONS = { droplets: Droplets, pill: Pill, brain: Brain, layers: Layers };

const STATE_COLORS = {
  calm_alignment: '#34d399', performative_flow: '#a78bfa', nervous_system_strain: '#f87171',
  cognitive_overload: '#fbbf24', emotional_fatigue: '#c084fc', recovery_debt: '#94a3b8',
  anxiety_loop: '#fb7185', fragmented_focus: '#fb923c', irritable_reactivity: '#ef4444',
};

export default function DemoControlCenter() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('personas');
  const [guidedFlow, setGuidedFlow] = useState(null); // Full-screen flow data
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/demo-reality/personas`),
        axios.get(`${API}/demo-reality/compare`),
      ]);
      setPersonas(pRes.data?.personas || []);
      setComparison(cRes.data?.comparison || []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/demo/seed`);
      await axios.post(`${API}/twinflo/internal/recompute-history-all?days=14`);
      await axios.post(`${API}/twinflo/internal/recompute-all`);
      await axios.post(`${API}/twinflo/t2/compute-all`);
      await axios.post(`${API}/twinflo/t3/compute-all`);
      toast.success('Demo reality fully seeded + computed');
      load();
    } catch { toast.error('Seed failed'); }
    setSeeding(false);
  };

  const startGuidedDemo = async (personaId) => {
    try {
      const res = await axios.get(`${API}/demo-reality/guided-flow/${personaId}`);
      setGuidedFlow(res.data);
    } catch { toast.error('Failed to load guided flow'); }
  };

  if (guidedFlow) {
    return <GuidedDemoFullScreen data={guidedFlow} onExit={() => setGuidedFlow(null)} navigate={navigate} />;
  }

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5" data-testid="demo-control-center">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Demo Reality Engine</h1>
            <p className="text-xs text-adm-tertiary mt-1">Immersive system storytelling for demos, training, and validation</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSeed} disabled={seeding} className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3" data-testid="seed-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {seeding ? 'Computing...' : 'Seed + Compute All'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/[0.06]">
          {[
            { key: 'personas', label: 'Personas', icon: Users },
            { key: 'compare', label: 'Compare', icon: Activity },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-['Work_Sans'] border-b-2 transition-colors ${tab === t.key ? 'border-adm-accent text-adm-accent' : 'border-transparent text-adm-tertiary'}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Personas Tab */}
        {tab === 'personas' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="persona-grid">
            {personas.map(p => {
              const Icon = PERSONA_ICONS[p.icon] || Brain;
              const stateColor = STATE_COLORS[p.twinflo?.state] || '#94a3b8';
              return (
                <Card key={p.id} className="bg-adm-surface border-white/[0.06] hover:border-white/[0.1] transition-colors overflow-hidden">
                  <CardContent className="p-0">
                    {/* Color band */}
                    <div className="h-1" style={{ backgroundColor: p.color }} />
                    <div className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}15` }}>
                          <Icon className="w-5 h-5" style={{ color: p.color }} />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-['Outfit'] font-semibold text-adm-primary">{p.name}</div>
                          <div className="text-[10px]" style={{ color: p.color }}>{p.label}</div>
                        </div>
                        <Badge className="text-[8px] bg-amber-500/15 text-amber-400 border-0">DEMO</Badge>
                      </div>

                      {/* TwinFlo Summary */}
                      <div className="grid grid-cols-4 gap-1.5">
                        {[
                          { label: 'State', value: p.twinflo?.state?.replace(/_/g, ' ').split(' ').map(w => w[0]?.toUpperCase()).join('') || '—', color: stateColor },
                          { label: 'Reg', value: p.twinflo?.regulation ?? '—', color: (p.twinflo?.regulation ?? 50) >= 40 ? '#34d399' : '#f87171' },
                          { label: 'Res', value: p.twinflo?.resilience ?? '—', color: (p.twinflo?.resilience ?? 50) >= 30 ? '#34d399' : '#fbbf24' },
                          { label: 'Preds', value: p.twinflo?.predictions ?? 0, color: '#fbbf24' },
                        ].map(s => (
                          <div key={s.label} className="bg-white/[0.02] rounded-lg p-1.5 text-center">
                            <div className="text-[8px] text-adm-tertiary">{s.label}</div>
                            <div className="text-xs font-semibold" style={{ color: s.color }}>{s.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button onClick={() => startGuidedDemo(p.id)} className="flex-1 bg-adm-accent/10 text-adm-accent hover:bg-adm-accent/20 text-[10px] h-7" data-testid={`guided-${p.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Start Demo
                        </Button>
                        <Button onClick={() => navigate(`/admin/twinflo-evolution/${p.id}`)} variant="ghost" className="text-adm-tertiary hover:text-adm-primary text-[10px] h-7">
                          <Eye className="w-3 h-3 mr-1" /> Evolution
                        </Button>
                        <Button onClick={() => navigate(`/admin/care-dashboard/${p.id}`)} variant="ghost" className="text-adm-tertiary hover:text-adm-primary text-[10px] h-7">
                          <Shield className="w-3 h-3 mr-1" /> Care
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Compare Tab */}
        {tab === 'compare' && (
          <div className="overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Person', 'Persona', 'State', 'Reg', 'Emo', 'Cog', 'Res', 'Pattern', 'Best Action', 'Top Risk'].map(h => (
                    <th key={h} className="py-2 px-2 text-left text-[9px] text-adm-tertiary uppercase tracking-wider font-['Work_Sans']">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.map(r => (
                  <tr key={r.id} className="border-b border-white/[0.03]">
                    <td className="py-2 px-2 text-adm-primary font-medium">{r.name}</td>
                    <td className="py-2 px-2"><Badge className="text-[8px] border-0" style={{ backgroundColor: `${r.color}15`, color: r.color }}>{r.persona}</Badge></td>
                    <td className="py-2 px-2"><div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_COLORS[r.state] || '#94a3b8' }} /><span className="text-adm-secondary">{r.state?.replace(/_/g, ' ')}</span></div></td>
                    <td className="py-2 px-2 font-semibold" style={{ color: (r.regulation ?? 50) >= 40 ? '#34d399' : '#f87171' }}>{r.regulation ?? '—'}</td>
                    <td className="py-2 px-2 font-semibold" style={{ color: (r.emotional_load ?? 0) <= 40 ? '#34d399' : '#fbbf24' }}>{r.emotional_load ?? '—'}</td>
                    <td className="py-2 px-2 font-semibold" style={{ color: (r.cognitive_strain ?? 0) <= 40 ? '#34d399' : '#fbbf24' }}>{r.cognitive_strain ?? '—'}</td>
                    <td className="py-2 px-2 font-semibold" style={{ color: (r.resilience ?? 50) >= 30 ? '#34d399' : '#f87171' }}>{r.resilience ?? '—'}</td>
                    <td className="py-2 px-2 text-adm-tertiary">{r.pattern}</td>
                    <td className="py-2 px-2 text-adm-secondary">{r.best_action}</td>
                    <td className="py-2 px-2 text-amber-400 text-[10px]">{r.top_prediction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════
// GUIDED DEMO — FULL-SCREEN IMMERSIVE
// ═══════════════════════════════════════════════════════════

function GuidedDemoFullScreen({ data, onExit, navigate }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const steps = data?.steps || [];
  const step = steps[currentStep] || {};
  const persona = data?.persona || {};
  const stateColor = step.data?.state ? (STATE_COLORS[step.data.state] || '#94a3b8') : (persona.color || '#c8b89a');

  // Auto-advance
  useEffect(() => {
    if (playing && steps.length > 0) {
      timerRef.current = setTimeout(() => {
        if (currentStep < steps.length - 1) setCurrentStep(p => p + 1);
        else setPlaying(false);
      }, step.duration_ms || 5000);
    }
    return () => clearTimeout(timerRef.current);
  }, [currentStep, playing, steps.length, step.duration_ms]);

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-[#060a10] flex flex-col" data-testid="guided-demo-fullscreen">
      {/* Progress bar */}
      <div className="h-0.5 bg-white/[0.04]">
        <div className="h-full bg-gradient-to-r from-adm-accent/40 to-adm-accent transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${persona.color || '#c8b89a'}15` }}>
            <Brain className="w-4 h-4" style={{ color: persona.color || '#c8b89a' }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: `${persona.color || '#c8b89a'}60` }}>TwinFlo Demo</div>
            <div className="text-sm font-['Outfit'] font-medium" style={{ color: '#c8b89a' }}>{data?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="text-[9px] bg-amber-500/10 text-amber-400 border-0">Guided Demo</Badge>
          <span className="text-[10px] text-white/20">{currentStep + 1} / {steps.length}</span>
          <button onClick={onExit} className="text-white/20 hover:text-white/60 transition-colors" data-testid="exit-demo">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-8">
        <div className="max-w-3xl w-full space-y-8">
          {/* Step title */}
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: `${stateColor}60` }}>{step.title}</div>
          </div>

          {/* Narration — the game-changer */}
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-['Outfit'] font-light leading-relaxed" style={{ color: '#c8b89aCC' }}>
              {step.narration}
            </p>
          </div>

          {/* Data visualization per step */}
          <StepVisual step={step} stateColor={stateColor} />
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 px-8 py-6">
        <button onClick={() => { setCurrentStep(Math.max(0, currentStep - 1)); setPlaying(false); }} disabled={currentStep === 0}
          className="text-white/20 hover:text-white/60 disabled:opacity-20 transition-colors" data-testid="prev-step">
          <SkipBack className="w-5 h-5" />
        </button>
        <button onClick={() => setPlaying(!playing)}
          className="w-12 h-12 rounded-full bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center transition-colors" data-testid="play-pause">
          {playing ? <Pause className="w-5 h-5 text-white/60" /> : <Play className="w-5 h-5 text-white/60 ml-0.5" />}
        </button>
        <button onClick={() => { setCurrentStep(Math.min(steps.length - 1, currentStep + 1)); setPlaying(false); }} disabled={currentStep >= steps.length - 1}
          className="text-white/20 hover:text-white/60 disabled:opacity-20 transition-colors" data-testid="next-step">
          <SkipForward className="w-5 h-5" />
        </button>
      </div>

      {/* Step dots */}
      <div className="flex items-center justify-center gap-1.5 pb-6">
        {steps.map((_, i) => (
          <button key={i} onClick={() => { setCurrentStep(i); setPlaying(false); }}
            className={`w-2 h-2 rounded-full transition-all ${i === currentStep ? 'bg-adm-accent w-6' : i < currentStep ? 'bg-white/20' : 'bg-white/[0.06]'}`} />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// STEP VISUAL — Data visualization per narrative step
// ═══════════════════════════════════════════════════════════

function StepVisual({ step, stateColor }) {
  const data = step.data;
  if (!data) return null;

  // State step — show score bars
  if (step.id === 'current_state' && data.scores) {
    return (
      <div className="flex justify-center">
        <div className="grid grid-cols-3 gap-4 max-w-md">
          {[
            { key: 'regulation_score', label: 'Regulation', good: 'high' },
            { key: 'emotional_load_score', label: 'Emotional Load', good: 'low' },
            { key: 'cognitive_strain_score', label: 'Cognitive Strain', good: 'low' },
          ].map(s => {
            const val = data.scores[s.key] ?? 50;
            const color = (s.good === 'high' ? val >= 40 : val <= 50) ? '#34d399' : '#fbbf24';
            return (
              <div key={s.key} className="text-center">
                <div className="text-3xl font-['Outfit'] font-bold" style={{ color }}>{val}</div>
                <div className="text-[9px] text-white/30 mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Drivers step
  if (step.id === 'drivers' && data.chain?.length) {
    return (
      <div className="flex items-center justify-center gap-3">
        {data.chain.slice(0, 4).map((d, i) => {
          const label = d.split(':').pop()?.replace(/_/g, ' ') || d;
          return (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-4 h-4 text-white/10" />}
              <div className="px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <span className="text-xs" style={{ color: '#c8b89a80' }}>{label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Timeline step
  if (step.id === 'timeline' && data.timeline?.length) {
    return (
      <div className="flex items-end justify-center gap-1 h-20">
        {data.timeline.map((t, i) => {
          const color = STATE_COLORS[t.state] || '#94a3b8';
          const reg = t.regulation ?? 50;
          return (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="w-4 rounded-t" style={{ height: `${Math.max(reg * 0.6, 6)}px`, backgroundColor: color, opacity: 0.8 }} />
            </div>
          );
        })}
      </div>
    );
  }

  // Predictions step
  if (step.id === 'predictions' && data.predictions?.length) {
    return (
      <div className="flex justify-center gap-3">
        {data.predictions.slice(0, 2).map((p, i) => (
          <div key={i} className={`rounded-xl px-5 py-3 border ${p.severity === 'positive' ? 'bg-emerald-950/20 border-emerald-500/10' : 'bg-amber-950/20 border-amber-500/10'}`}>
            <div className={`text-sm font-['Outfit'] font-medium ${p.severity === 'positive' ? 'text-emerald-400' : 'text-amber-400'}`}>{p.label}</div>
            <div className="text-[10px] text-white/30 mt-1">{Math.round(p.probability * 100)}% • {p.time_horizon}</div>
          </div>
        ))}
      </div>
    );
  }

  // Response curves step
  if (step.id === 'response_curves' && data.curves?.length) {
    return (
      <div className="flex justify-center gap-3">
        {data.curves.slice(0, 3).map((c, i) => (
          <div key={i} className="rounded-xl px-4 py-3 bg-white/[0.02] border border-white/[0.06] text-center">
            <div className="text-xs text-white/60">{c.action}</div>
            <div className="text-2xl font-['Outfit'] font-bold mt-1" style={{ color: c.effectiveness >= 0.6 ? '#34d399' : '#fbbf24' }}>
              {Math.round(c.effectiveness * 100)}%
            </div>
            <div className="text-[9px] text-white/20 mt-0.5">{c.speed}</div>
          </div>
        ))}
      </div>
    );
  }

  // Conclusion
  if (step.id === 'conclusion') {
    return (
      <div className="flex justify-center gap-6">
        {['T1: Understand', 'T2: Predict', 'T3: Learn', 'T4: Simulate'].map((t, i) => (
          <div key={i} className="text-center">
            <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: ['#34d39920', '#fbbf2420', '#c084fc20', '#60a5fa20'][i] }}>
              <span className="text-xs font-bold" style={{ color: ['#34d399', '#fbbf24', '#c084fc', '#60a5fa'][i] }}>{`T${i + 1}`}</span>
            </div>
            <div className="text-[9px] text-white/30">{t.split(': ')[1]}</div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}
